import { diggingAjax } from '../ajax/DiggingAjax'
import { store } from '../state/store'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { DateTime } from 'luxon'

import { TruckLoadDTO } from '../DTOs/Production/TruckLoadDTO'
import type { TruckLoadMutationResultDTO } from '../DTOs/Production/TruckLoadMutationResultDTO'
import { computeShiftId } from './computeShiftId'
import { enqueue, queuedItemFor } from './queueOps'
import { nextTempId } from './tempId'
import { v4 as uuidv4 } from 'uuid'
import { isOnlineNow } from './connectivity'
import { mayQueueOffline, offlineRefusal } from './offlineWrites'
import { sendDirect } from './directSave'
import { addQueuedLoadToCachedDiggingSummary } from './patchCachedDiggingSummary'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

function currentShift(): { date: DateTime; shift: string } {
  const { selectedDate, selectedShift } = store.getState().shift
  return { date: DateTime.fromISO(selectedDate), shift: selectedShift }
}

export const loadingsMutations = {
  async insertTruckLoad(dto: TruckLoadDTO) {
    const clientId = uuidv4()
    const sentDirect = isOnlineNow()
    if (sentDirect) {
      const sent = await sendDirect(() => diggingAjax.insertTruckLoad(dto, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    const tempId = uuidv4()
    const tempLoadingId = await nextTempId()
    await enqueue({
      clientId,
      sentDirect,
      entity: 'loading',
      operation: 'create',
      shiftId: currentShiftId(),
      tempId,
      entityId: tempLoadingId,
      data: { ...dto },
    })
    const optimisticLoad = new TruckLoadDTO({ ...dto, loadingId: tempLoadingId })
    const { date, shift } = currentShift()
    addQueuedLoadToCachedDiggingSummary(dto, date, shift)
    return {
      success: true,
      data: { truckLoad: optimisticLoad, currentHourTotalLoads: 0, shiftTotalLoads: 0 } as TruckLoadMutationResultDTO | null,
    }
  },

  async updateTruckLoad(dto: TruckLoadDTO, previous?: { loads: number; tonnes?: number }) {
    const clientId = uuidv4()
    if (isOnlineNow()) {
      const sent = await sendDirect(() => diggingAjax.updateTruckLoad(dto, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()

    if (!dto.loadingId) {
      console.error('Refusing to queue a truck load update with no loadingId', dto)
      return {
        success: false,
        data: null,
        errors: [{ code: 0, description: 'That load could not be saved offline because it has no id yet. Re-enter it and try again.' }],
      } as ActionResponse<TruckLoadMutationResultDTO | null>
    }
    const queued = await queuedItemFor('loading', dto.loadingId)
    const lastCounted = (queued?.data as { loads?: number; tonnes?: number } | undefined) ?? previous
    await enqueue({
      clientId,
      entity: 'loading',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: dto.loadingId,
      rowVersion: dto.rowVersion,
      data: { ...dto },
    })
    if (lastCounted) {
      const { date, shift } = currentShift()
      addQueuedLoadToCachedDiggingSummary({
        diggingPatternId: dto.diggingPatternId,
        loads: (dto.loads ?? 0) - (lastCounted.loads ?? 0),
        tonnes: (dto.tonnes ?? 0) - (lastCounted.tonnes ?? 0),
      }, date, shift)
    }
    return {
      success: true,
      data: { truckLoad: dto, currentHourTotalLoads: 0, shiftTotalLoads: 0 } as TruckLoadMutationResultDTO | null,
    }
  },

  async deleteTruckLoad(loadingId: number, rowVersion?: string, deleted?: { diggingPatternId: number; loads: number; tonnes?: number }) {
    const clientId = uuidv4()
    if (isOnlineNow()) {
      const sent = await sendDirect(() => diggingAjax.deleteTruckLoad(loadingId, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    await enqueue({
      clientId,
      entity: 'loading',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: loadingId,
      rowVersion,
    })
    if (deleted) {
      const { date, shift } = currentShift()
      addQueuedLoadToCachedDiggingSummary(deleted, date, shift, -1)
    }
    return {
      success: true,
      data: { truckLoad: null, currentHourTotalLoads: 0, shiftTotalLoads: 0 } as TruckLoadMutationResultDTO | null,
    }
  },
}
